import React, { useEffect } from 'react'
import ReactGA from 'react-ga'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/react-hooks'
import { OutpostRestaurant } from './Outpost.graphql'

const OutpostContext = React.createContext({ isOutpost: false })

export const OutpostProvider = ({ diningOptionGuid, children }) => {
  // clearer, consumer-friendly way to check if we are in the outpost experience
  // also, might check more than just the existence of diningOptionGuid in the future
  const isOutpost = Boolean(diningOptionGuid)

  const context = {
    isOutpost,
    diningOptionGuid
  }

  // Dimension tracks whether events are in outpost mode
  useEffect(() => {
    ReactGA.set({
      dimension12: isOutpost
    })
  }, [isOutpost])

  return (
    <OutpostContext.Provider value={context}>
      {children}
    </OutpostContext.Provider>
  )
}

OutpostProvider.propTypes = {
  children: PropTypes.element.isRequired,
  diningOptionGuid: PropTypes.string
}

export const OutpostConsumer = ({ children }) => (
  <OutpostContext.Consumer>
    {context => children(context)}
  </OutpostContext.Consumer>
)

OutpostConsumer.propTypes = {
  children: PropTypes.func.isRequired
}

export const MockOutpostProvider = ({
  mocks: {
    diningOptionGuid
  },
  children
}) => {
  const isOutpost = Boolean(diningOptionGuid)
  const context = {
    diningOptionGuid,
    isOutpost
  }

  return (
    <OutpostContext.Provider value={context}>
      {children}
    </OutpostContext.Provider>
  )
}

export const useOutpost = () => React.useContext(OutpostContext)
export const useOutpostRestaurant = () => {
  const { isOutpost, diningOptionGuid } = useOutpost()
  const { data, loading: outpostLoading } = useQuery(OutpostRestaurant, {
    variables: {
      outpostRestaurantInput: { diningOptionGuid: diningOptionGuid }
    },
    skip: !isOutpost
  })

  const { outpostRestaurant } = data || {}
  if (outpostLoading || !outpostRestaurant) {
    return { outpostLoading, isOutpost }
  }
  const { outpost = {}, orderableNow, daySchedule = {} } = outpostRestaurant
  const { outpostName } = outpost
  const { deliveryTime, deliveryDateTime } = daySchedule
  return {
    outpostLoading,
    isOutpost,
    outpostName,
    outpostOrderableNow: orderableNow,
    outpostDeliveryTime: deliveryTime,
    outpostDeliveryDateTime: deliveryDateTime
  }
}

MockOutpostProvider.propTypes = {
  children: PropTypes.node.isRequired,
  mocks: PropTypes.shape({
    diningOptionGuid: PropTypes.string
  })
}
